import React from 'react';

/**
 * Renders the `examples` from a task descriptor as a small illustration table.
 * Each row shows: compact stimulus → arrow → response label → optional note.
 * Returns null when the descriptor has no examples (graceful degradation).
 *
 * TaskExamples always passes compact={true} to StimulusView. The game never
 * uses TaskExamples — it renders StimulusView directly without compact — so
 * this component cannot affect in-game rendering.
 *
 * Accessibility: every row carries its meaning in text (response label + note)
 * as well as visually. Stroop examples include a note naming the ink colour so
 * the lesson survives greyscale and screen readers.
 */
export default function TaskExamples({ task }) {
  if (!task?.examples?.length) return null;

  const { CueView, StimulusView, examples } = task;

  return (
    <div
      style={{
        margin: '0 auto var(--space-5)',
        maxWidth: 360,
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <div
        style={{
          fontSize: 'var(--text-label-size)',
          fontWeight: 700,
          color: 'var(--text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: 2,
        }}
      >
        Examples
      </div>
      {examples.map((ex, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            background: 'var(--glass-bg-medium)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--space-2) var(--space-3)',
          }}
        >
          {/* Compact stimulus — reuses the real renderer, never drifts from game */}
          <div
            style={{
              minWidth: 52,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-2)',
            }}
          >
            {CueView && <CueView stimulus={ex.stimulus} compact />}
            <StimulusView stimulus={ex.stimulus} compact />
          </div>
          <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-body-size)' }}>→</span>
          <div style={{ flex: 1 }}>
            <span
              style={{
                fontSize: 'var(--text-body-size)',
                fontWeight: 700,
                color: 'var(--text-primary)',
              }}
            >
              {ex.response}
            </span>
            {ex.note && (
              <span
                style={{
                  marginLeft: 'var(--space-2)',
                  fontSize: 'var(--text-numeric-size)',
                  color: 'var(--text-tertiary)',
                }}
              >
                {ex.note}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
